import { shallowReactive, type ShallowReactive } from 'vue';

export type UseAsyncRes<A extends unknown[], R> = [
  state: ShallowReactive<{
    isLoading: boolean;
    error: unknown;
    value: R | null;
  }>,
  fn: (...args: A) => Promise<R>,
];
/**
 *  useAsync wraps an async function and exposes reactive isLoading, error, and value so the UI can track the request.
 *  It returns that state plus a wrapped caller that updates those fields as the promise runs, succeeds, or fails.
 */
export const useAsync = <A extends unknown[], R>(fn: (...args: A) => Promise<R>): UseAsyncRes<A, R> => {
  const state = shallowReactive({
    isLoading: false,
    error: undefined,
    value: undefined as R | null,
  });

  return [
    state,
    async (...args: A) => {
      state.isLoading = true;
      state.error = null;
      state.value = null;
      try {
        state.value = await fn(...args);
        return state.value;
      } catch (err) {
        state.error = err;
        throw state.error;
      } finally {
        state.isLoading = false;
      }
    },
  ];
};
